import { isEmpty } from 'remeda'
import type { VariantImg } from '@velasca/types'
import { setQueryParams } from './setQueryParams'

interface GetProductCardParams {
  /**
   * The array of images for the product.
   */
  images: string[]
  /**
   * The custom product type of the product.
   */
  customProductType?: number
  /**
   * The front image index picked by the content creator. (array is 1-based).
   */
  frontImageIndex?: number
  /**
   * The hover image index picked by the content creator. (array is 1-based).
   */
  hoverImageIndex?: number
  /**
   * A boolean indicating if the front and hover image should be swapped.
   */
  areCollectionCardImagesWornApparel?: boolean
}

/**
 * This function returns the front and hover image for the product card.
 * If the product is not an apparel type, its images could follow the new or old naming convention.
 * With the old nomenclature images will have the following format: _08 or _09,
 * otherwise, the new nomenclature will be used, which will have the following format: ZX or ZZ.
 * If the front or hover image is not found, the function will try to find the image by index.
 * If the front or hover image index is 0, the function will log a warning and fall back to automatic detection.
 * If the images array is empty, the function will return an empty string.
 * If the areCollectionCardImagesWornApparel is true, the function will swap the front and hover images.
 *
 * @param obj -  The object with the parameters for getting a product card image.
 * @returns The front and hover image for the product card.
 */
export const getProductCardImage = ({
  images,
  customProductType,
  frontImageIndex,
  hoverImageIndex,
  areCollectionCardImagesWornApparel = false,
}: GetProductCardParams): VariantImg => {
  if (isEmpty(images)) {
    return { front: '', hover: '', swatch: '' }
  }

  const frontIndex = areCollectionCardImagesWornApparel ? 0 : 3
  const hoverIndex = areCollectionCardImagesWornApparel ? 3 : 0

  let front = images[frontIndex]
  let hover = images[hoverIndex]

  // If the product is not an apparel type, its images could follow the new or old naming convention.
  // With the old nomenclature images will have the following format: _08 or _09,
  // otherwise, the new nomenclature will be used, which will have the following format: ZX or ZZ.
  if (customProductType !== 3) {
    front =
      images.find((image) => image.includes('_08.') || image.includes('ZX_')) ||
      ''
    hover =
      images.find((image) => image.includes('_09.') || image.includes('ZZ_')) ||
      ''
  }

  // If the front or hover image is not found, the function will try to find the image by index.
  if (!front) {
    if (frontImageIndex === 0) {
      console.warn(
        `getProductCardImage: frontImageIndex should start from 1, not 0. Falling back to automatic detection. Product images: ${String(images.length)} total, first image: ${images[0]?.slice(0, 100) ?? 'none'}...`,
      )
      // Use the original automatic detection result
      front = images[frontIndex] || ''
    } else if (frontImageIndex && frontImageIndex > 0) {
      front = images[frontImageIndex - 1] || ''
    }
  }

  if (!hover) {
    if (hoverImageIndex === 0) {
      console.warn(
        `getProductCardImage: hoverImageIndex should start from 1, not 0. Falling back to automatic detection. Product images: ${String(images.length)} total, first image: ${images[0]?.slice(0, 100) ?? 'none'}...`,
      )
      // Use the original automatic detection result
      hover = images[hoverIndex] || ''
    } else if (hoverImageIndex && hoverImageIndex > 0) {
      hover = images[hoverImageIndex - 1] || ''
    }
  }

  let swatch = front

  if (front) {
    swatch = setQueryParams(front, [{ name: 'width', value: '70' }])
  }

  return { front, hover, swatch }
}
